import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ChevronRight, MoreVertical, Trash2, FileText, Image as ImageIcon, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { queueAndFlush, getQueuedDeletedIds } from "@/lib/syncQueue";

// Order history — moved out of Settings onto the Orders page
export function OrderHistoryDialog({ open, onOpenChange, onOrdersChanged }) {
  const [orderHistory, setOrderHistory] = useState([]);
  const [histSearch, setHistSearch] = useState("");
  const [expandedHistId, setExpandedHistId] = useState(null);
  const [deleteOrderId, setDeleteOrderId] = useState(null);
  const [moveOrderId, setMoveOrderId] = useState(null);

  const fetchOrderHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/orders/history`, { timeout: 15000 });
      const deleted = getQueuedDeletedIds('orders');
      const clean = deleted.length > 0 ? data.filter(o => !deleted.includes(o.id)) : data;
      setOrderHistory(clean);
      try { localStorage.setItem('order_history_cache', JSON.stringify(clean)); } catch {}
    } catch {
      try {
        const cache = JSON.parse(localStorage.getItem('order_history_cache') || '[]');
        const deleted = getQueuedDeletedIds('orders');
        setOrderHistory(cache.filter(o => !deleted.includes(o.id)));
      } catch {}
    }
  };

  useEffect(() => { if (open) fetchOrderHistory(); }, [open]);

  const deleteOrder = (id) => {
    setOrderHistory(prev => prev.filter(o => o.id !== id));
    queueAndFlush({ method: "delete", path: `/orders/${id}`, entity: "orders", entityId: id });
    toast.success("Deleted");
  };

  const moveOrderToOrders = (id) => {
    setOrderHistory(prev => prev.filter(o => o.id !== id));
    queueAndFlush({
      method: "put", path: `/orders/${id}`,
      body: { completed: false, completed_at: null, done: true, done_at: new Date().toISOString() },
      entity: "orders", entityId: id,
    });
    try {
      const c = JSON.parse(localStorage.getItem('order_history_cache') || '[]');
      localStorage.setItem('order_history_cache', JSON.stringify(c.filter(o => o.id !== id)));
    } catch { /* ignore */ }
    toast.success("Moved to Orders page");
    setTimeout(() => { onOrdersChanged && onOrdersChanged(); }, 800);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg w-[calc(100vw-2rem)] max-h-[85vh] overflow-y-auto [&>button]:hidden" data-testid="order-history-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Order History</DialogTitle>
            <button onClick={() => onOpenChange(false)} className="p-1.5 -mt-1 -mr-1 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 transition-colors flex-shrink-0" data-testid="order-history-close-x">
              <X className="w-6 h-6" />
            </button>
          </div>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={histSearch} onChange={(e) => setHistSearch(e.target.value)}
            placeholder="Search customer, month, date, pallet type…"
            className="rounded-none border-zinc-300 h-9 text-sm"
            data-testid="order-history-search" />
          {(() => {
            const fmtD = (ds) => { if (!ds) return ''; const d = new Date(ds + 'T12:00:00'); return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };
            const fmtTime = (s) => { if (!s || s <= 0) return null; const h = Math.floor(s/3600); const m = Math.floor((s%3600)/60); return h > 0 ? `${h}h ${m}m` : `${m}m`; };
            const dateStrings = (ds) => {
              if (!ds) return '';
              const d = new Date(String(ds).includes('T') ? ds : ds + 'T12:00:00');
              if (isNaN(d.getTime())) return String(ds);
              return [String(ds), d.toLocaleDateString(),
                d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`,
                `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`,
                `${d.getMonth() + 1}/${d.getFullYear()}`].join(' ');
            };
            const haystack = (o) => [
              o.customer_name || '', o.notes || '',
              (o.pallet_items || []).map(i => `${i.pallet_type_name || ''} ${i.quantity || ''}`).join(' '),
              o.pallet_type_name || '',
              dateStrings(o.completed_at), dateStrings(o.load_date), dateStrings(o.deliver_date),
            ].join(' ').toLowerCase();
            const tokens = histSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
            const filtered = tokens.length === 0 ? orderHistory : orderHistory.filter(o => { const h = haystack(o); return tokens.every(t => h.includes(t)); });
            if (orderHistory.length === 0) return <p className="text-sm text-zinc-400 text-center py-6">No completed orders</p>;
            if (filtered.length === 0) return <p className="text-sm text-zinc-400 text-center py-6" data-testid="order-history-no-results">No orders match &quot;{histSearch}&quot;</p>;
            const groups = [];
            filtered.forEach(o => {
              const d = o.completed_at ? new Date(o.completed_at) : null;
              const key = d && !isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'No date';
              const last = groups[groups.length - 1];
              if (last && last.key === key) last.orders.push(o); else groups.push({ key, orders: [o] });
            });
            return (
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              {groups.map(g => (
                <div key={g.key}>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 bg-zinc-100 border border-zinc-200 px-2 py-1.5 mb-1.5 sticky top-0" data-testid={`hist-month-${g.key.replace(/\s/g, '-')}`}>
                    {g.key} <span className="text-zinc-400 font-normal">· {g.orders.length} order{g.orders.length === 1 ? '' : 's'}</span>
                  </p>
                  <div className="space-y-1.5">
              {g.orders.map(o => {
                const files = o.files || (o.file_url ? [{ url: o.file_url, name: o.file_name }] : []);
                const isExpanded = expandedHistId === o.id;
                return (
                <div key={o.id} className="border border-zinc-200 bg-white">
                  <div className="flex items-center gap-2 px-2 py-2">
                    <button onClick={() => setExpandedHistId(isExpanded ? null : o.id)} className="flex items-center gap-2 flex-1 min-w-0 text-left" data-testid={`hist-order-toggle-${o.id}`}>
                      <ChevronRight className={`w-4 h-4 text-zinc-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      <span className="text-xs font-bold uppercase tracking-wider text-zinc-700 truncate">{o.customer_name || 'Unnamed'}</span>
                      {o.completed_at && <span className="text-[9px] text-zinc-400 flex-shrink-0 ml-auto">{new Date(o.completed_at).toLocaleDateString()}</span>}
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 text-zinc-400 hover:text-zinc-600 transition-colors flex-shrink-0" data-testid={`order-hist-menu-${o.id}`}>
                          <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-none border-zinc-200">
                        <DropdownMenuItem onClick={() => setMoveOrderId(o.id)} className="rounded-none text-sm py-2 cursor-pointer" data-testid={`move-order-hist-${o.id}`}>
                          <FileText className="w-3.5 h-3.5 mr-2" />Move To Orders Page
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteOrderId(o.id)} className="text-red-600 focus:text-red-600 focus:bg-red-50 rounded-none text-sm py-2 cursor-pointer" data-testid={`delete-order-hist-${o.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-2" />Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {isExpanded && (
                  <div className="px-3 pb-3 border-t border-zinc-100 pt-2">
                    {(() => {
                      const items = o.pallet_items && o.pallet_items.length > 0 ? o.pallet_items : (o.pallet_type_name ? [{ pallet_type_name: o.pallet_type_name, quantity: o.quantity }] : []);
                      return items.length > 0 ? (
                        <div className="mb-1.5 space-y-0.5" data-testid={`hist-pallets-${o.id}`}>
                          {items.map((item, ii) => (
                            <div key={ii} className="flex items-center justify-between gap-2 text-[10px]">
                              <span className="text-zinc-700 font-semibold truncate">{item.pallet_type_name}</span>
                              {item.quantity > 0 && <span className="text-zinc-800 font-bold flex-shrink-0">{Number(item.quantity).toLocaleString()}</span>}
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[10px]">
                      {o.load_date && <><span className="text-zinc-400">Load</span><span className="text-zinc-700">{fmtD(o.load_date)}</span></>}
                      {o.deliver_date && <><span className="text-zinc-400">Deliver</span><span className="text-zinc-700">{fmtD(o.deliver_date)}</span></>}
                      {fmtTime(o.cut_elapsed) && <><span className="text-zinc-400">Cut Time</span><span className="text-zinc-700">{fmtTime(o.cut_elapsed)}{o.cut_guys > 1 ? ` (${o.cut_guys} guys)` : ''}</span></>}
                      {fmtTime(o.assembly_elapsed) && <><span className="text-zinc-400">Assembly</span><span className="text-zinc-700">{fmtTime(o.assembly_elapsed)}{o.assembly_guys > 1 ? ` (${o.assembly_guys} guys)` : ''}</span></>}
                      {(o.stopwatches || []).filter(sw => (sw.elapsed || 0) > 0).map(sw => (
                        <React.Fragment key={sw.id}>
                          <span className="text-zinc-400">{sw.title || 'Timer'}</span>
                          <span className="text-zinc-700">{fmtTime(sw.elapsed)}{sw.guys > 1 ? ` (${sw.guys} guys)` : ''}</span>
                        </React.Fragment>
                      ))}
                      {(() => {
                        const tot = (o.cut_elapsed || 0) + (o.assembly_elapsed || 0) + (o.stopwatches || []).reduce((s, sw) => s + (sw.elapsed || 0), 0);
                        return (o.stopwatches || []).length > 0 && tot > 0 ? <><span className="text-zinc-500 font-bold">Total Time</span><span className="text-zinc-900 font-bold">{fmtTime(tot)}</span></> : null;
                      })()}
                      {o.completed_at && <><span className="text-zinc-400">Completed</span><span className="text-zinc-700">{new Date(o.completed_at).toLocaleDateString()}</span></>}
                    </div>
                    {o.notes && <p className="text-[10px] text-zinc-400 mt-1 italic">{o.notes}</p>}
                    {files.length > 0 && (
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {(() => {
                          const isImg = (f) => /\.(png|jpg|jpeg|gif|webp|heic|heif)$/i.test(f.name || '');
                          const imgCount = files.filter(isImg).length;
                          let imgNum = 0;
                          return files.map((f, fi) => {
                            const img = isImg(f);
                            if (img) imgNum += 1;
                            const label = img ? (imgCount > 1 ? `Image ${imgNum}` : 'Image') : f.name;
                            return (
                              <a key={fi} href={`${API.replace('/api', '')}${f.url}`} target="_blank" rel="noopener noreferrer"
                                className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5" data-testid={`hist-file-${o.id}-${fi}`}>
                                {img ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />} {label}
                              </a>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                  )}
                </div>
                );
              })}
                  </div>
                </div>
              ))}
            </div>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>

    {/* Delete Order History Confirmation */}
    <AlertDialog open={!!deleteOrderId} onOpenChange={(o) => { if (!o) setDeleteOrderId(null); }}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Delete Order?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">Are you sure you want to delete this completed order? This cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { deleteOrder(deleteOrderId); setDeleteOrderId(null); }} className="bg-red-600 hover:bg-red-700 text-white rounded-none">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Move Order Back To Orders Page Confirmation */}
    <AlertDialog open={!!moveOrderId} onOpenChange={(o) => { if (!o) setMoveOrderId(null); }}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="move-order-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Move To Orders Page?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            This order will move back to the Orders page as a finished / ready-for-pickup order. No order notification will be sent.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300" data-testid="move-order-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { moveOrderToOrders(moveOrderId); setMoveOrderId(null); }} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-none" data-testid="move-order-confirm">Move</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
